import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  IonHeader,
  NavController,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonButton,
  IonFooter,
  IonBackdrop,
  IonToolbar,
  IonBackButton,
  ModalController,
  IonButtons,
  AlertController,
  ToastController,
} from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, from, map, switchMap, take } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  injectParam,
  injectQueryParam,
} from 'src/app/utils/injectionsUtils/activatedRouteUtils';
import { Message } from 'src/app/features/chat/types/message';
import {
  ChatAiStatus,
  CHAT_AI_STATUS_WORLD_BUILDING,
} from 'src/app/features/chat/types/chat-ai-status';
import { ChatService } from 'src/app/features/chat/services/chat.service';
import { ChatContentComponent } from 'src/app/features/chat/components/chat-content/chat-content.component';
import {
  SendSuggestedRollPayload,
  SendSuggestedMessagePayload,
} from 'src/app/features/chat/components/message/message.component';
import { SuggestedActionEntry } from 'src/app/features/chat/types/extra-data';
import { buildOperationChatSpan } from 'src/app/features/mce-editor/extensions/operation/operation-chat-span';
import { StoryService } from 'src/app/features/stories/story.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Camera, GalleryPhoto } from '@capacitor/camera';
import { ModalEditorComponent } from '../../components/modal-editor/modal-editor.component';
import { FooterEditorComponent } from '../../components/footer-editor/footer-editor.component';
import { injectQuotables } from './utils/injectQuotables';
import { injectHideFab } from './utils/injectHideFab';
import {
  IMPERSONATE_CHAT_ID,
  SelectImpersonateService,
} from 'src/app/features/chat/services/select-impersonate.service';
import { ProfilePage } from 'src/app/pages/profile/profile.page';
import { CharacterPage } from 'src/app/pages/characters/pages/character/character.page';
import { CreateAiCharacterPage } from 'src/app/pages/stories/pages/story/pages/create-ai-character/create-ai-character.page';
import { ChatTitleComponent } from '../../components/chat-title/chat-title.component';
import { AuthService } from 'src/app/features/auth/auth.service';
import type { StorySetupAnswers, StorySetupWizardPhase } from '../../components/story-setup-wizard/story-setup.types';
import { StoryAiSetupService } from 'src/app/features/stories/story-ai-setup.service';
import { isStorySetupMessage } from '../../utils/story-setup-message.utils';
import { parseSetupPreferences } from '../../utils/setup-preferences';


import { environment } from 'src/environments/environment';
import { AppForegroundService } from 'src/app/features/app-lifecycle/app-foreground.service';
import { UsePwaService } from 'src/app/features/pwa/pwa-service';
import { RecentChatsService } from 'src/app/features/chat/services/recent-chats.service';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { PosthogService } from 'src/app/features/analytics/posthog.service';
import { DESKTOP_MIN_WIDTH_PX } from 'src/app/utils/responsive-nav-animation';
import { ChatReadService } from '../../services/chat-read.service';
import { BackNavigationService } from 'src/app/features/common/services/back-navigation.service';
import {
  CreditsService,
  isInsufficientCreditsError,
} from 'src/app/features/credits/credits.service';

@Component({
  selector: 're-chat',
  templateUrl: './chat.page.html',
  styleUrls: ['./chat.page.scss'],
  providers: [
    {
      provide: ChatService,
      useFactory: () => {
        let message = injectQueryParam('message');
        return ChatService.factory(
          Number(injectParam('chat')),
          Number(message),
        );
      },
    },
    {
      provide: IMPERSONATE_CHAT_ID,
      useFactory: () => Number(injectParam('chat')),
    },
    SelectImpersonateService,
  ],
  imports: [
    ChatTitleComponent,
    CommonModule,
    FormsModule,
    ChatContentComponent,
    IonContent,
    IonFab,
    IonIcon,
    IonButton,
    IonButtons,
    IonFooter,
    IonBackdrop,
    IonFabButton,
    IonToolbar,
    IonHeader,
    IonBackButton,
    RouterLink,
    ModalEditorComponent,
    FooterEditorComponent,
    ...TRANSLATE_IMPORTS,
  ],
})
export class ChatPage implements AfterViewInit, OnDestroy {
  backNavigation = inject(BackNavigationService);
  chatComponent = viewChild(ChatContentComponent);
  ionContent = viewChild.required(IonContent);
  ionContentEl = viewChild.required(IonContent, { read: ElementRef });
  ionFooterEl = viewChild(IonFooter, { read: ElementRef });
  modaleEditor = viewChild(ModalEditorComponent);
  footerEditor = viewChild(FooterEditorComponent);

  impersonateService = inject(SelectImpersonateService, { optional: true });

  injector = inject(Injector);
  navController = inject(NavController);
  modalController = inject(ModalController);
  alertController = inject(AlertController);
  private toastController = inject(ToastController);
  private translate = inject(TranslateService);
  private credits = inject(CreditsService);
  chat = Number(injectParam('chat'));
  backChatId = this.chat;
  private chatService = inject(ChatService);
  private readonly chatRead = inject(ChatReadService);
  private readonly chatVisible = signal(false);
  /** Forces TinyMCE iframe recreation after the app returns from background. */
  readonly editorRefreshVersion = signal(0);
  activeChatData = this.chatService.chatData;
  masterLoading = this.chatService.masterLoading;
  story = inject(StoryService, { optional: true });
  isAiStory = computed(() => this.story?.data()?.ai_mode === 'AI');
  isLoreChat = computed(() => this.activeChatData()?.kind === 'lore');
  isStoryAiChat = computed(
    () => this.isAiStory() && this.activeChatData()?.kind === 'story',
  );
  /** Solo le conversazioni dirette tra amici usano le bolle compatte e orientate. */
  usePrivateMessageLayout = computed(() => !!this.activeChatData()?.friendship);
  chatWritePlaceholder = computed(() =>
    this.story?.isTableSession()
      ? this.translate.instant('chat.writePlaceholderTableSession')
      : this.translate.instant('chat.writePlaceholder'),
  );
  private chatLoaded = toSignal(this.chatService.firstBatchLoaded, { initialValue: false });
  isChatEmpty = computed(() => this.chatLoaded() && this.chatService.list().length === 0);

  isStoryCreator = computed(
    () => this.story?.data()?.creator === this.profile()?.id,
  );
  isStorySetupInProgress = computed(
    () =>
      this.isStoryAiChat() &&
      this.chatLoaded() &&
      this.chatService.hasStorySetupMessage() &&
      !this.chatService.hasGameplayMasterMessage(),
  );
  isStoryAwaitingStart = computed(
    () =>
      this.isStoryAiChat() &&
      this.chatLoaded() &&
      !this.chatService.hasStorySetupMessage() &&
      !this.chatService.hasVisibleMasterMessage(),
  );
  showStorySetupWizard = computed(() => false);
  /** Creatore: setup completo ma PG mancante — pulsante, non wizard inline. */
  showCreateCharacterCta = computed(
    () =>
      this.isStoryAiChat() &&
      this.chatLoaded() &&
      this.isStoryCreator() &&
      !this.chatService.hasGameplayMasterMessage() &&
      this.chatService.hasStorySetupMessage() &&
      !!this.story?.settingBrief() &&
      !this.currentUserHasCharacter(),
  );
  showStartAdventureCta = computed(
    () =>
      this.isStoryAiChat() &&
      this.chatLoaded() &&
      this.isStoryCreator() &&
      !this.chatService.hasGameplayMasterMessage() &&
      this.chatService.hasStorySetupMessage() &&
      !!this.story?.settingBrief() &&
      this.currentUserHasCharacter(),
  );
  showSetupWizardOverlay = computed(() => false);
  storySetupWizardPhase = computed((): StorySetupWizardPhase => {
    if (!this.chatService.hasStorySetupMessage()) return 'preferences';
    if (!this.story?.settingBrief()) return 'waitingSetting';
    return 'character';
  });
  showWaitingForHost = computed(
    () => this.isStoryAwaitingStart() && !this.isStoryCreator(),
  );
  showStoryStartingWait = computed(
    () =>
      this.isStoryAiChat() &&
      this.chatLoaded() &&
      this.isStoryCreator() &&
      this.chatService.hasStorySetupMessage() &&
      !this.story?.settingBrief() &&
      !this.chatService.hasGameplayMasterMessage() &&
      (this.chatService.masterBusy() || this.storySetupSubmitting()),
  );
  showSettingRetryCta = computed(
    () =>
      this.isStoryAiChat() &&
      this.chatLoaded() &&
      this.isStoryCreator() &&
      this.chatService.hasStorySetupMessage() &&
      !this.story?.settingBrief() &&
      !this.chatService.hasGameplayMasterMessage() &&
      !this.chatService.masterBusy() &&
      !this.storySetupSubmitting(),
  );
  hideChatFooter = computed(
    () =>
      this.isStoryAiChat() && !this.chatService.hasGameplayMasterMessage(),
  );
  currentUserHasCharacter = computed(() => {
    const uid = this.profile()?.id;
    if (!uid) return false;
    return (this.story?.characters.data() ?? []).some((c) => c.owner === uid);
  });
  storyOwnerNames = computed(() => {
    const map = new Map<string, string>();
    for (const u of this.story?.users() ?? []) {
      map.set(u.id, u.displayName);
    }
    return map;
  });
  storySetupSubmitting = signal(false);
  storyCharacters = computed(() => this.story?.characters.data() ?? []);
  savedStorySetupAnswers = computed((): StorySetupAnswers | null => {
    const fromColumn = parseSetupPreferences(
      this.story?.data()?.setup_preferences,
    );
    if (fromColumn) return fromColumn;
    const msg = this.chatService.rawList().find((m) => isStorySetupMessage(m));
    return parseSetupPreferences(msg?.extra_data) ?? this.story?.storySetupAnswers() ?? null;
  });

  profile = inject(AuthService).profileSignal;
  private http = inject(HttpClient);
  private foreground = inject(AppForegroundService);
  private pwa = inject(UsePwaService);
  private router = inject(Router);
  private recentChatsService = inject(RecentChatsService);
  private setupService = inject(StoryAiSetupService);
  private posthog = inject(PosthogService);

  quotables = injectQuotables(
    this.activeChatData,
    this.story?.characters.data ?? signal([]),
  );
  imageSelected = signal<GalleryPhoto | undefined>(undefined);
  backdrop = signal<boolean>(false);
  text = signal<string>('');
  masterOverlayDismissed = signal<boolean>(false);
  masterBusy = this.chatService.masterBusy;
  showMasterOverlay = computed(
    () => this.masterLoading() && !this.masterOverlayDismissed(),
  );

  /** Icona da mostrare nell'overlay/FAB in base allo stato AI. */
  masterOverlayIcon = computed(() => {
    return this.chatService.aiStatus() === CHAT_AI_STATUS_WORLD_BUILDING
      ? 'globe-outline'
      : this.isLoreChat()
        ? 'help-circle-outline'
        : 'sparkles-outline';
  });

  private readonly masterStatuses: Exclude<ChatAiStatus, null>[] = [
    'recalling',
    'coherence',
    'drafting',
    'finalizing',
    'worldbuilding',
  ];

  private readonly loreStatuses: Exclude<ChatAiStatus, null>[] = [
    'recalling',
    'drafting',
    'finalizing',
  ];

  /** Elenco degli stati da mostrare come step indicator. */
  overlayStatuses = computed(() =>
    this.isLoreChat() ? this.loreStatuses : this.masterStatuses,
  );

  /** Indice dello stato attivo tra gli stati mostrati. */
  overlayActiveStatusIndex = computed(() => {
    const status = this.chatService.aiStatus();
    if (status === null) return -1;
    return this.overlayStatuses().indexOf(status);
  });

  /** Chiave di traduzione del titolo dell'overlay per lo stato corrente. */
  masterOverlayTitleKey = computed(() => {
    const status = this.chatService.aiStatus() ?? 'recalling';
    const prefix = this.isLoreChat() ? 'chat.loreOverlay' : 'chat.masterOverlay';
    return `${prefix}.${status}Title`;
  });

  /** Chiave di traduzione del body dell'overlay per lo stato corrente. */
  masterOverlayBodyKey = computed(() => {
    const status = this.chatService.aiStatus() ?? 'recalling';
    const prefix = this.isLoreChat() ? 'chat.loreOverlay' : 'chat.masterOverlay';
    return `${prefix}.${status}Body`;
  });

  private footerHeight = signal<number>(0);
  private fabListHeightCache: number | null = null;
  quotedMessage: WritableSignal<Message | undefined> = signal(undefined);

  viewInit: BehaviorSubject<boolean> = new BehaviorSubject(false);
  hideFab: Signal<boolean | undefined> = injectHideFab(
    this.chatComponent,
    this.viewInit,
    this.injector,
  );
  getScrollContainer = this.viewInit.pipe(
    filter((v) => v),
    switchMap(() => from(this.ionContent().getScrollElement())),
    take(1),
  );

  routeData$ = inject(ActivatedRoute).data;
  allowSettings = toSignal(
    this.routeData$.pipe(map((data) => data['settings'] ?? false)),
  );
  allowCharacters = toSignal(
    this.routeData$.pipe(map((data) => data['characters'] ?? false)),
  );

  allowBookmark = toSignal(
    this.routeData$.pipe(map((data) => data['bookmarks'] ?? false)),
  );

  allowChaptersData = toSignal(
    this.routeData$.pipe(map((data) => data['chapters'] ?? false)),
  );

  allowChapters = computed(() => {
    return (
      this.allowChaptersData() &&
      !this.isAiStory() &&
      this.story?.data()?.creator === this.profile()?.id
    );
  });

  dismissMasterOverlay() {
    this.masterOverlayDismissed.set(true);
  }

  openMasterOverlay() {
    this.masterOverlayDismissed.set(false);
  }

  private handleMasterInvoke409(err: HttpErrorResponse): void {
    this.chatService.syncAiStatusFromServer();
    if (err.error?.code === 'MASTER_ALREADY_RESPONDED') {
      void this.showMasterAlreadyRespondedToast();
    }
  }

  private handleCreditInvokeError(err: HttpErrorResponse): boolean {
    if (!isInsufficientCreditsError(err)) {
      return false;
    }
    this.chatService.resetMasterInvokeOnClientError();
    const price = Number(err.error?.price);
    const available = Number(err.error?.available);
    const action =
      err.error?.action === 'lore_ai_invoke'
        ? 'lore_ai_invoke'
        : 'master_ai_invoke';
    void this.credits.presentInsufficient({
      kind: action === 'lore_ai_invoke' ? 'lore' : 'master',
      price: Number.isFinite(price) ? price : this.credits.priceFor(action),
      available: Number.isFinite(available) ? available : this.credits.available(),
    });
    return true;
  }

  /** Hold già creata lato server (202): riallinea il saldo senza aspettare il realtime. */
  private onAiInvokeAccepted(): void {
    void this.credits.refreshWallet();
  }

  private async showMasterAlreadyRespondedToast(): Promise<void> {
    const toast = await this.toastController.create({
      message: this.translate.instant('chat.invokeMaster.alreadyResponded'),
      duration: 3500,
      position: 'bottom',
      color: 'warning',
    });
    await toast.present();
  }

  constructor() {
    effect(() => {
      if (this.masterLoading()) {
        untracked(() => this.masterOverlayDismissed.set(false));
      }
    });

    effect(() => {
      if (!this.isStoryAiChat() || !this.chatLoaded() || !this.isStoryCreator()) {
        return;
      }
      if (this.chatService.hasGameplayMasterMessage()) return;
      if (this.story?.settingBrief()) return;
      const storyId = this.story?.id();
      if (storyId == null) return;
      untracked(() => {
        void this.router.navigateByUrl(`/stories/${storyId}/setup`, {
          replaceUrl: true,
        });
      });
    });

    effect(() => {
      const data = this.activeChatData();
      untracked(() => {
        if (data)
          this.recentChatsService.trackChat({
            chatId: data.id,
            chatName: this.getRecentChatName(data),
            storyName: data.story?.name ?? null,
            route: this.router.url.split('?')[0],
            openedAt: Date.now(),
            kind: this.getRecentChatKind(data.kind, !!data.story),
          });
      });
    });

    effect(() => {
      const base = this.footerHeight();
      const fabOpen = this.backdrop();
      let extra = 0;
      if (fabOpen) {
        if (this.fabListHeightCache === null) {
          const fabListEl = this.ionFooterEl()?.nativeElement.querySelector(
            'ion-fab-list',
          ) as HTMLElement | null;
          if (fabListEl) {
            this.fabListHeightCache = fabListEl.getBoundingClientRect().height;
          }
        }
        extra = this.fabListHeightCache ?? 0;
      }
      const total = base + extra;
      untracked(() => {
        const contentEl = this.ionContentEl().nativeElement as HTMLElement;
        contentEl.style.setProperty('--offset-bottom', total + 'px');
      });
    });

    effect((onCleanup) => {
      const footerEl = this.ionFooterEl()?.nativeElement as HTMLElement | undefined;
      if (!footerEl) {
        untracked(() => this.footerHeight.set(0));
        return;
      }
      const observer = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect.height ?? 0;
        this.footerHeight.set(h);
      });
      observer.observe(footerEl);
      onCleanup(() => observer.disconnect());
    });

    effect(() => {
      if (!this.chatVisible()) return;
      const latestId = this.chatService.rawList().reduce((max, message) => {
        const id = typeof message.id === 'number' ? message.id : 0;
        return Math.max(max, id);
      }, 0);
      if (latestId > 0) void this.chatRead.markRead(this.chat, latestId);
    });
  }

  private getRecentChatKind(
    kind: string | null | undefined,
    belongsToStory: boolean,
  ): 'narrator' | 'archivist' | 'inn' | 'private' | 'story' {
    if (kind === 'lore') return 'archivist';
    if (kind === 'story') return 'narrator';
    if (belongsToStory) return 'story';
    return this.router.url.startsWith('/inn/') ? 'inn' : 'private';
  }

  private getRecentChatName(data: NonNullable<ReturnType<typeof this.activeChatData>>): string {
    if (!data.friendship) return data.name;

    const otherUser =
      data.friendship.user1?.id === this.profile()?.id
        ? data.friendship.user2
        : data.friendship.user1;
    return otherUser?.displayName?.trim() || data.name;
  }

  ngAfterViewInit() {
    this.viewInit.next(true);
  }

  /** Foreground sync and read tracking while this chat is open (stack-safe). */
  ionViewWillEnter(): void {
    this.chatVisible.set(true);
    this.pwa.markChatOpened();
    if (Number.isFinite(this.chat)) {
      this.foreground.registerChat(
        this.chat,
        () => this.chatService.syncOnForeground(),
        () => this.editorRefreshVersion.update((version) => version + 1),
      );
    }
  }

  ionViewWillLeave(): void {
    this.chatVisible.set(false);
    this.foreground.unregisterChat();
  }

  sendMessage() {
    if ((true && this.text()) || this.imageSelected()) {
      this.posthog.action('send_message', 'try', {
        chatId: this.chat,
        storyId: this.story?.id(),
        hasText: !!this.text(),
        hasImage: !!this.imageSelected(),
        hasQuote: !!this.quotedMessage(),
        impersonateCharacterId: this.impersonateService?.impersonate()?.id ?? null,
      });
      let date = new Date().toISOString();
      this.chatComponent()
        ?.chatService.sendNewMessage(
          {
            chatId: this.chat ?? '',
            type: 'me',
            impersonate: this.impersonateService?.impersonate()?.id ?? null,
            localId: null,
            createdAt: date,
            updatedAt: date,
            text: this.text() + '',
            showUserInfo: true,
            image: this.imageSelected()?.webPath ?? null,
            meanings: null,
            messageQuoteId: Number(this.quotedMessage()?.id) ?? null,
            messageQuoted: this.quotedMessage(),
            extra_data: null,
          },
          this.imageSelected(),
        )
        .subscribe(() => {});
      this.imageSelected.set(undefined);
      this.quoteMessageHandler(undefined);
      this.text.set('');
      this.modaleEditor()?.modal?.dismiss();
    }
  }

  onSendSuggestedRoll(payload: SendSuggestedRollPayload) {
    this.posthog.action('send_suggested_roll', 'try', {
      chatId: this.chat,
      storyId: this.story?.id(),
      characterId: payload.characterId,
      operationName: payload.named,
    });
    this.text.set(
      buildOperationChatSpan(
        payload.named,
        payload.characterId,
        payload.characterName,
      ),
    );
    this.sendMessage();
  }

  onSuggestedAction(entry: SuggestedActionEntry) {
    switch (entry.action) {
      case 'createCharacter':
        this.posthog.event('chat_suggested_action_create_character', {
          storyId: this.story?.id(),
          chatId: this.chat,
          action: entry.action,
        });
        this.navigateCreateCharacter();
        break;
    }
  }

  onSendSuggestedMessage(payload: SendSuggestedMessagePayload) {
    this.posthog.action('send_suggested_message', 'try', {
      chatId: this.chat,
      storyId: this.story?.id(),
      characterId: payload.characterId,
      messageLength: payload.message.length,
    });
    this.impersonateService?.setImpersonate(payload.characterId);
    const chatService = this.chatComponent()?.chatService;
    if (!chatService || !this.chat) return;

    const date = new Date().toISOString();
    chatService
      .sendNewMessage(
        {
          chatId: this.chat,
          type: 'me',
          impersonate: this.impersonateService?.impersonate()?.id ?? null,
          localId: null,
          createdAt: date,
          updatedAt: date,
          text: payload.message,
          showUserInfo: true,
          image: null,
          meanings: null,
          messageQuoteId: Number(this.quotedMessage()?.id) ?? null,
          messageQuoted: this.quotedMessage(),
          extra_data: null,
        },
        undefined,
      )
      .subscribe({
        next: () =>
          this.posthog.action('send_suggested_message', 'success', {
            chatId: this.chat,
            storyId: this.story?.id(),
            characterId: payload.characterId,
          }),
        error: () =>
          this.posthog.action('send_suggested_message', 'error', {
            chatId: this.chat,
            storyId: this.story?.id(),
            characterId: payload.characterId,
          }),
      });
    this.quoteMessageHandler(undefined);
    this.text.set('');
    this.modaleEditor()?.modal?.dismiss();
  }

  private async navigateCreateCharacter() {
    const storyId = this.story?.id();
    if (!storyId) return;
    const modal = await this.modalController.create({
      component: CreateAiCharacterPage,
      componentProps: {
        story: storyId,
        inModal: true,
        openSheetAfterCreate: false,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss<{ characterId?: number }>();
    if (data?.characterId != null) {
      this.onCharacterReady();
    }
  }

  openCreateCharacter() {
    this.navigateCreateCharacter();
  }

  onCharacterReady() {
    void this.story?.characters.refetch();
  }

  onRetrySetting() {
    if (this.storySetupSubmitting()) return;
    const storyId = this.story?.id();
    const storyChatId = this.chat;
    if (!storyId || !storyChatId) return;

    this.storySetupSubmitting.set(true);
    void this.setupService
      .retryGenerateSetting(storyId, storyChatId)
      .then(() => void this.story?.updateStory())
      .catch(() => undefined)
      .finally(() => this.storySetupSubmitting.set(false));
  }

  onPreferencesCommitted(answers: StorySetupAnswers) {
    if (this.storySetupSubmitting()) return;
    const storyId = this.story?.id();
    const storyChatId = this.chat;
    if (!storyId || !storyChatId) return;

    this.storySetupSubmitting.set(true);
    void this.setupService
      .commitPreferencesAndGenerateSetting(
        {
          storyId,
          storyChatId,
          ownerNames: this.storyOwnerNames(),
        },
        answers,
      )
      .then(() => {
        void this.story?.refreshStorySetupPresence();
        void this.story?.updateStory();
        void this.story?.characters.refetch();
      })
      .catch(async () => {
        const toast = await this.toastController.create({
          message: this.translate.instant('chat.storySetup.commitError'),
          duration: 4000,
          position: 'bottom',
          color: 'danger',
        });
        await toast.present();
      })
      .finally(() => this.storySetupSubmitting.set(false));
  }

  onStartAdventure() {
    const answers = this.savedStorySetupAnswers();
    if (!answers) return;
    this.onStorySetupCompleted(answers);
  }

  onStorySetupCompleted(_answers: StorySetupAnswers) {
    if (this.storySetupSubmitting()) return;
    const storyId = this.story?.id();
    if (!storyId || !this.currentUserHasCharacter()) return;

    this.storySetupSubmitting.set(true);
    this.posthog.action('story_setup_submit', 'try', {
      storyId,
      chatId: this.chat,
    });

    this.chatService.beginMasterInvoke();
    this.http
      .post(
        `${environment.masterServerUrl}/api/${storyId}/${this.chat}/master`,
        {},
      )
      .subscribe({
        next: () => {
          this.posthog.action('story_setup_submit', 'success', {
            storyId,
            chatId: this.chat,
          });
          this.storySetupSubmitting.set(false);
          this.onAiInvokeAccepted();
        },
        error: (err: HttpErrorResponse) => {
          this.posthog.action('story_setup_submit', 'error', {
            storyId,
            chatId: this.chat,
          });
          this.storySetupSubmitting.set(false);
          if (this.handleCreditInvokeError(err)) {
            return;
          }
          if (err.status === 409) {
            this.handleMasterInvoke409(err);
            return;
          }
          this.chatService.resetMasterInvokeOnClientError();
        },
      });
  }

  showProfile(id: string) {
    this.modalController
      .create({
        component: ProfilePage,
        componentProps: {
          id,
        },
      })
      .then((modal) => modal.present());
  }
  showCharacter(id: number) {
    this.modalController
      .create({
        component: CharacterPage,
        componentProps: {
          story: this.story?.id(),
          id,
          inModal: true,
        },
      })
      .then((modal) => modal.present());
  }

  quoteMessageHandler(message: Message | undefined) {
    this.quotedMessage.set(message);
  }

  addImage() {
    Camera.pickImages({
      quality: 90,
      height: 1500,
      limit: 1,
    }).then((image) => {
      this.imageSelected.set(image.photos.at(0) ?? undefined);
    });
  }

  removeImage() {
    this.imageSelected.set(undefined);
  }

  saveBookmark(message: Message, isChapter: boolean = false) {
    this.alertController
      .create({
        header: isChapter
          ? this.translate.instant('chat.bookmark.saveChapter')
          : this.translate.instant('chat.bookmark.save'),
        inputs: [
          {
            name: 'name',
            placeholder: isChapter
              ? this.translate.instant('chat.bookmark.chapterTitlePlaceholder')
              : this.translate.instant('chat.bookmark.titlePlaceholder'),
            type: 'text',
          },
        ],
        buttons: [
          {
            text: this.translate.instant('common.save'),
            role: 'save',
          },
          {
            text: this.translate.instant('common.cancel'),
            role: 'cancel',
          },
        ],
      })
      .then((alertEl) => {
        alertEl.present();
        return alertEl.onWillDismiss();
      })
      .then((data) => {
        if ((data.role === 'save', data.data?.values.name)) {
          const eventName = isChapter ? 'save_chapter' : 'save_bookmark';
          this.posthog.action(eventName, 'try', {
            storyId: this.story?.id(),
            chatId: this.chat,
            messageId: message.id,
          });
          if (this.story)
            this.story
              .saveBookmark(
                message.id,
                data.data.values.name,
                isChapter ? undefined : this.profile()?.id,
              )
              .then((res) => {
                if (res) {
                  this.posthog.action(eventName, 'success', {
                    storyId: this.story?.id(),
                    chatId: this.chat,
                    messageId: message.id,
                  });
                  this.chatComponent()
                    ?.chatViewport()
                    ?.updateElement({
                      ...message,
                      bookmarks: (message?.bookmarks ?? []).concat([
                        {
                          title: data.data.values.name,
                          user: isChapter ? undefined : this.profile()?.id,
                        },
                      ]),
                    });
                } else {
                  this.posthog.action(eventName, 'error', {
                    storyId: this.story?.id(),
                    chatId: this.chat,
                    messageId: message.id,
                  });
                }
              });
        }
      });
  }

  removeBookmark(message: Message, isChapter: boolean = false) {
    this.alertController
      .create({
        header: isChapter
          ? this.translate.instant('chat.bookmark.removeChapter')
          : this.translate.instant('chat.bookmark.remove'),
        buttons: [
          {
            text: this.translate.instant('common.remove'),
            handler: () => {
              const eventName = isChapter ? 'remove_chapter' : 'remove_bookmark';
              this.posthog.action(eventName, 'try', {
                storyId: this.story?.id(),
                chatId: this.chat,
                messageId: message.id,
              });
              if (this.story)
                this.story.removeBookmark(message.id, isChapter).then((res) => {
                  if (res) {
                    this.posthog.action(eventName, 'success', {
                      storyId: this.story?.id(),
                      chatId: this.chat,
                      messageId: message.id,
                    });
                    this.chatComponent()
                      ?.chatViewport()
                      ?.updateElement({
                        ...message,
                        bookmarks: (message?.bookmarks ?? []).filter((el) =>
                          isChapter
                            ? el.user === undefined
                            : el.user !== this.profile()?.id,
                        ),
                      });
                  }
                });
            },
          },
          {
            text: this.translate.instant('common.cancel'),
            role: 'cancel',
          },
        ],
      })
      .then((aler) => aler.present());
  }

  callStorySetting() {
    const storyId = this.story?.id();
    if (storyId == null || !this.isStoryAiChat()) return;
    if (this.masterBusy()) {
      this.openMasterOverlay();
      return;
    }
    this.posthog.action('story_setup_setting', 'try', {
      storyId,
      chatId: this.chat,
    });
    this.chatService.beginMasterInvoke();
    this.http
      .post(
        `${environment.masterServerUrl}/api/${storyId}/${this.chat}/story-setting`,
        {},
      )
      .subscribe({
        next: () => {
          void this.story?.updateStory();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409 && err.error?.code === 'STORY_SETTING_ALREADY_SET') {
            void this.story?.updateStory();
            this.chatService.syncAiStatusFromServer();
            return;
          }
          if (err.status === 409) {
            this.chatService.syncAiStatusFromServer();
            return;
          }
          this.chatService.resetMasterInvokeOnClientError();
        },
      });
  }

  callMaster() {
    const storyId = this.story?.id();
    if (storyId == null || !this.isStoryAiChat()) return;
    if (this.masterBusy()) {
      this.openMasterOverlay();
      return;
    }
    this.posthog.action('call_master', 'try', {
      storyId,
      chatId: this.chat,
    });
    this.chatService.beginMasterInvoke();
    this.http
      .post(
        `${environment.masterServerUrl}/api/${storyId}/${this.chat}/master`,
        {},
      )
      .subscribe({
        next: () => this.onAiInvokeAccepted(),
        error: (err: HttpErrorResponse) => {
          if (this.handleCreditInvokeError(err)) {
            return;
          }
          if (err.status === 409) {
            this.handleMasterInvoke409(err);
            return;
          }
          this.chatService.resetMasterInvokeOnClientError();
        },
      });
  }

  callLore() {
    const storyId = this.story?.id();
    if (storyId == null || !this.isLoreChat()) return;
    if (this.masterBusy()) {
      this.openMasterOverlay();
      return;
    }
    this.posthog.action('call_lore', 'try', {
      storyId,
      chatId: this.chat,
    });
    this.chatService.beginMasterInvoke();
    this.http
      .post(
        `${environment.masterServerUrl}/api/${storyId}/${this.chat}/lore`,
        {},
      )
      .subscribe({
        next: () => this.onAiInvokeAccepted(),
        error: (err: HttpErrorResponse) => {
          if (this.handleCreditInvokeError(err)) {
            return;
          }
          if (err.status === 409) {
            this.chatService.syncAiStatusFromServer();
            return;
          }
          this.chatService.resetMasterInvokeOnClientError();
        },
      });
  }

  openModal() {
    const modal = this.modaleEditor()?.modal;
    if (!modal) {
      return;
    }

    if (typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH_PX) {
      void modal.present();
      return;
    }

    modal.setCurrentBreakpoint(1);
    void modal.present();
  }
  toggleFab() {
    this.backdrop.set(false);
    this.footerEditor()?.fabList.close();
  }

  openBackdrop() {
    this.backdrop.update((val) => !val);
  }

  ngOnDestroy(): void {
    this.chatVisible.set(false);
    this.foreground.unregisterChat();
    this.modaleEditor()?.modal?.dismiss();
    this.fabListHeightCache = null;
  }
}
